import React, { useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import AppButton from "../utils/Appbutton";
import branding from "../config/branding";
import footer from "../utils/footer";
import { resetPassword } from "../services/auth";

const validationSchema = Yup.object({
  password: Yup.string()
    .min(6, "Senha deve ter no mínimo 6 caracteres")
    .required("Senha é obrigatória"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Senhas não coincidem")
    .required("Confirmação de senha é obrigatória"),
});

const ResetPasswordScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const token = searchParams.get("token") || "";

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!token) {
      setError("Link de recuperação inválido.");
      return;
    }

    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setFieldErrors({});
      setLoading(true);
      const response = await resetPassword({
        token,
        password: formData.password,
      });
      setSuccess(response?.message || "Senha atualizada com sucesso.");
      setFormData({ password: "", confirmPassword: "" });
      window.setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (err) {
      if (err?.inner) {
        const nextErrors = {};
        err.inner.forEach((item) => {
          nextErrors[item.path] = item.message;
        });
        setFieldErrors(nextErrors);
      } else {
        setError(
          err?.response?.data?.message || "Não foi possível atualizar a senha.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={6} sx={{ borderRadius: "12px", overflow: "hidden" }}>
          <Box
            sx={{
              background: "linear-gradient(135deg, #9c27b0 0%, #757575 100%)",
              color: "white",
              padding: 3,
              textAlign: "center",
            }}
          >
            <Box
              component="img"
              src={branding.logo}
              alt="Logo"
              sx={{ height: 180, width: 180, borderRadius: "15%" }}
            />
          </Box>

          <CardContent sx={{ padding: 4 }}>
            <Stack spacing={2}>
              <Typography variant="h5" fontWeight={700} textAlign="center">
                Definir nova senha
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Escolha uma nova senha para concluir a recuperação da conta.
              </Typography>

              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <Box sx={{ position: "relative" }}>
                    <TextField
                      fullWidth
                      name="password"
                      label="Nova senha"
                      type={showPassword ? "text" : "password"}
                      variant="filled"
                      value={formData.password}
                      onChange={handleChange}
                      error={Boolean(fieldErrors.password)}
                      helperText={fieldErrors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: "#9c27b0" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      sx={{
                        position: "absolute",
                        right: 30,
                        top: "50%",
                        transform: "translateY(-50%)",
                        padding: 0.5,
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Box>

                  <TextField
                    fullWidth
                    name="confirmPassword"
                    label="Confirmar senha"
                    type={showPassword ? "text" : "password"}
                    variant="filled"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.confirmPassword)}
                    helperText={fieldErrors.confirmPassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#9c27b0" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <AppButton
                      type="submit"
                      valueText="Alterar senha"
                      loading={loading}
                    />
                  </Box>
                </Stack>
              </form>

              <Typography variant="body2" textAlign="center">
                <Link
                  to="/"
                  style={{ color: "#9c27b0", textDecoration: "none" }}
                >
                  Voltar ao login
                </Link>
              </Typography>
            </Stack>
          </CardContent>
        </Card>
        {footer()}
      </Container>
    </Box>
  );
};

export default ResetPasswordScreen;
